import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  GraduationCap, Sparkles, ArrowLeft, Loader2, AlertCircle, 
  Target, FileText, BookOpen, Lightbulb, Copy, Check
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

import { API_URL } from '../utils/api';

export function AIPlaygroundPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);

  // Goal generation state
  const [goalArea, setGoalArea] = useState('');
  const [studentContext, setStudentContext] = useState('');

  // PWN state
  const [pwnData, setPwnData] = useState({
    action_type: 'propose',
    action_description: '',
    reasons: '',
    evaluations_used: '',
    options_considered: ''
  });

  // General draft state
  const [draftPrompt, setDraftPrompt] = useState('');
  const [draftContext, setDraftContext] = useState('');

  const handleGenerateGoal = async () => {
    if (!goalArea || !studentContext) {
      setError('Please fill in all required fields');
      return;
    }
    setLoading(true);
    setError('');
    setResult('');
    try {
      const response = await axios.post(`${API_URL}/api/ai/generate-goal`, {
        goal_area: goalArea,
        student_context: { description: studentContext },
        additional_context: null
      }, { withCredentials: true });
      setResult(response.data.goal_draft || response.data.error || 'No response');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate goal');
    } finally {
      setLoading(false);
    }
  };

  const handleDraftPWN = async () => {
    if (!pwnData.action_description || !pwnData.reasons) {
      setError('Please fill in required fields');
      return;
    }
    setLoading(true);
    setError('');
    setResult('');
    try {
      const response = await axios.post(`${API_URL}/api/ai/draft-pwn`, {
        ...pwnData,
        evaluations_used: pwnData.evaluations_used.split(',').map(s => s.trim()).filter(Boolean),
        options_considered: pwnData.options_considered.split(',').map(s => s.trim()).filter(Boolean)
      }, { withCredentials: true });
      setResult(response.data.pwn_draft || response.data.error || 'No response');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to draft PWN');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneralDraft = async () => {
    if (!draftPrompt) {
      setError('Please enter a prompt');
      return;
    }
    setLoading(true);
    setError('');
    setResult('');
    try {
      const response = await axios.post(`${API_URL}/api/ai/draft`, {
        prompt: draftPrompt,
        context: draftContext || null
      }, { withCredentials: true });
      setResult(response.data.draft || response.data.error || 'No response');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate draft');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-[#F3F4F1] rounded-lg" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5 text-[#595959]" />
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-7 w-7 text-[#2C4C3B]" />
              <span className="font-outfit text-xl font-semibold text-[#1C1C1C]">FrameEd</span>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-6 lg:p-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#E2DACD]/50 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#6B5D4D]" />
            </div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-[#1C1C1C]">AI Playground</h1>
          </div>
          <p className="text-[#595959]">Explore FrameEd's AI-powered drafting and suggestion capabilities</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto">×</button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Panel */}
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6">
            <Tabs defaultValue="goals" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="goals" className="flex items-center gap-2" data-testid="tab-goals">
                  <Target className="h-4 w-4" />
                  Goals
                </TabsTrigger>
                <TabsTrigger value="pwn" className="flex items-center gap-2" data-testid="tab-pwn">
                  <FileText className="h-4 w-4" />
                  PWN
                </TabsTrigger>
                <TabsTrigger value="draft" className="flex items-center gap-2" data-testid="tab-draft">
                  <Lightbulb className="h-4 w-4" />
                  Draft
                </TabsTrigger>
              </TabsList>

              <TabsContent value="goals" className="space-y-4">
                <div className="space-y-2">
                  <Label>Goal Area *</Label>
                  <Select value={goalArea} onValueChange={setGoalArea}>
                    <SelectTrigger className="rounded-xl" data-testid="goal-area-select">
                      <SelectValue placeholder="Select goal area" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reading">Reading</SelectItem>
                      <SelectItem value="math">Math</SelectItem>
                      <SelectItem value="writing">Writing</SelectItem>
                      <SelectItem value="speech-language">Speech/Language</SelectItem>
                      <SelectItem value="social-emotional">Social-Emotional</SelectItem>
                      <SelectItem value="behavior">Behavior</SelectItem>
                      <SelectItem value="motor">Motor Skills</SelectItem>
                      <SelectItem value="adaptive">Adaptive/Life Skills</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Student Context *</Label>
                  <Textarea
                    value={studentContext}
                    onChange={(e) => setStudentContext(e.target.value)}
                    placeholder="Describe the student's current performance level, needs, and any relevant background..."
                    className="rounded-xl min-h-[150px]"
                    data-testid="student-context-input"
                  />
                </div>
                <Button 
                  onClick={handleGenerateGoal}
                  disabled={loading}
                  className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
                  data-testid="generate-goal-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Generate Goal
                </Button>
              </TabsContent>

              <TabsContent value="pwn" className="space-y-4">
                <div className="space-y-2">
                  <Label>Action Type</Label>
                  <Select value={pwnData.action_type} onValueChange={(v) => setPwnData({...pwnData, action_type: v})}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="propose">Propose</SelectItem>
                      <SelectItem value="refuse">Refuse</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Action Description *</Label>
                  <Textarea
                    value={pwnData.action_description}
                    onChange={(e) => setPwnData({...pwnData, action_description: e.target.value})}
                    placeholder="Describe the action being proposed or refused..."
                    className="rounded-xl min-h-[80px]"
                    data-testid="pwn-action-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reasons *</Label>
                  <Textarea
                    value={pwnData.reasons}
                    onChange={(e) => setPwnData({...pwnData, reasons: e.target.value})}
                    placeholder="Explain the reasons for this action..."
                    className="rounded-xl min-h-[80px]"
                    data-testid="pwn-reasons-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Evaluations Used (comma separated)</Label>
                  <Textarea
                    value={pwnData.evaluations_used}
                    onChange={(e) => setPwnData({...pwnData, evaluations_used: e.target.value})}
                    placeholder="Psychological evaluation, Academic assessment..."
                    className="rounded-xl"
                  />
                </div>
                <Button 
                  onClick={handleDraftPWN}
                  disabled={loading}
                  className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
                  data-testid="draft-pwn-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                  Draft PWN
                </Button>
              </TabsContent>

              <TabsContent value="draft" className="space-y-4">
                <div className="space-y-2">
                  <Label>What would you like help with? *</Label>
                  <Textarea
                    value={draftPrompt}
                    onChange={(e) => setDraftPrompt(e.target.value)}
                    placeholder="e.g., Draft a present levels statement for a student with autism who excels in math but struggles with reading comprehension..."
                    className="rounded-xl min-h-[120px]"
                    data-testid="draft-prompt-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Additional Context (optional)</Label>
                  <Textarea
                    value={draftContext}
                    onChange={(e) => setDraftContext(e.target.value)}
                    placeholder="Any additional context or requirements..."
                    className="rounded-xl min-h-[80px]"
                  />
                </div>
                <Button 
                  onClick={handleGeneralDraft}
                  disabled={loading}
                  className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
                  data-testid="generate-draft-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lightbulb className="h-4 w-4 mr-2" />}
                  Generate Draft
                </Button>
              </TabsContent>
            </Tabs>
          </div>

          {/* Output Panel */}
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-outfit text-lg font-semibold text-[#1C1C1C]">AI Output</h2>
              {result && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  className="text-[#595959] hover:text-[#1C1C1C]"
                  data-testid="copy-result-btn"
                >
                  {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              )}
            </div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B] mb-4" />
                <p className="text-[#595959]">Generating with AI...</p>
              </div>
            ) : result ? (
              <div 
                className="prose prose-sm max-w-none text-[#1C1C1C] whitespace-pre-wrap"
                data-testid="ai-result"
              >
                {result}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <BookOpen className="h-12 w-12 text-[#E5E5E5] mb-4" />
                <p className="text-[#595959]">Select a tool and enter your information to generate AI-powered content</p>
              </div>
            )}
          </div>
        </div>

        {/* Capabilities Info */}
        <div className="mt-8 bg-[#F3F4F1] rounded-2xl p-6">
          <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C] mb-4">Available AI Capabilities</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Target, title: 'Goal Generation', desc: 'SMART IEP goals with benchmarks' },
              { icon: FileText, title: 'PWN Drafting', desc: 'Prior Written Notice content' },
              { icon: Lightbulb, title: 'Present Levels', desc: 'Educational impact statements' },
              { icon: BookOpen, title: 'Accommodations', desc: 'Personalized suggestions' }
            ].map((cap) => (
              <div key={cap.title} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#2C4C3B]/10 flex items-center justify-center flex-shrink-0">
                  <cap.icon className="h-4 w-4 text-[#2C4C3B]" />
                </div>
                <div>
                  <p className="font-medium text-[#1C1C1C] text-sm">{cap.title}</p>
                  <p className="text-xs text-[#595959]">{cap.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
